import {ButtonStyleType} from '@udemy-v2/react-core-components';
import {FormGroup, TextInputForm, TextInputProps} from '@udemy-v2/react-form-components';
import {Loader} from '@udemy-v2/react-reveal-components';
import classNames from 'classnames';
import React, {useRef, useEffect} from 'react';
import {useI18n} from '@udemy-v2/i18n';

import {MODES, ModesKeys} from './constants';

import styles from './coupon-form.module.scss';

interface CouponFormProps {
    onChange: (evt: React.ChangeEvent<HTMLInputElement>) => void;
    onSubmit: () => void;
    disabled?: boolean;
    error?: string;
    submittedCode: string;
    inputCode: string;
    currentMode: ModesKeys;
    isInCheckoutPane?: boolean;
    size?: TextInputProps['size'];
    submitButtonStyle?: ButtonStyleType;
}

export const CouponForm = ({
    onChange,
    onSubmit,
    disabled = false,
    error,
    submittedCode,
    inputCode,
    currentMode,
    isInCheckoutPane = false,
    size,
    submitButtonStyle,
}: CouponFormProps) => {
    const {gettext} = useI18n();
    const inputRef = useRef<HTMLInputElement>(null);

    const submitButtonProps: {
        disabled: boolean;
        udStyle?: ButtonStyleType;
        className?: string;
    } = {
        disabled: disabled || currentMode === MODES.PENDING,
        udStyle: submitButtonStyle,
    };

    let submitButtonContent: React.ReactNode = gettext('Apply');

    if (isInCheckoutPane) {
        submitButtonProps.udStyle = 'secondary';
        submitButtonProps.className =
            currentMode === MODES.PENDING ? styles.submitButtonLoadingInCheckoutPane : undefined;
        submitButtonContent =
            currentMode === MODES.PENDING ? (
                <Loader size="small" color="inherit" />
            ) : (
                gettext('Apply')
            );
    }

    useEffect(() => {
        if (error && inputRef.current) {
            inputRef.current.focus();
        }
    }, [error]);

    const handleSubmit = () => {
        onSubmit();
    };

    return (
        <FormGroup
            label={gettext('Apply coupon')}
            labelProps={{className: 'ud-sr-only'}}
            className={styles['coupon-form-group']}
        >
            <TextInputForm
                submitButtonContent={submitButtonContent}
                onSubmit={handleSubmit}
                submitButtonProps={submitButtonProps}
                onChange={onChange}
                placeholder={error ? submittedCode : gettext('Enter Coupon')}
                size={size ?? (isInCheckoutPane ? 'small' : 'medium')}
                value={inputCode}
                disabled={currentMode === MODES.PENDING || disabled}
                data-purpose="coupon-input"
                aria-describedby={error ? 'coupon-error' : undefined}
                aria-label={gettext('Enter Coupon')}
                aria-invalid={!!error}
                ref={inputRef}
            />
            {error && (
                <p
                    id="coupon-error"
                    data-purpose="coupon-form-error"
                    className={classNames('ud-text-xs', styles['code-error'])}
                    role="alert"
                >
                    {error}
                </p>
            )}
        </FormGroup>
    );
};
